// BCM-898 · PA5 — Consumed material not matching BMS PO BoM material (slides 13, 14). [Uday]
// Status 38 ("Material Not Expired"; next status "Consumed Material Matching").
// Two tabs: "Consumed Material Not Matching PO BOM" (default, badge) and "View
// Consumption Document"; top-right Sync Inventory / Sync PO / Resubmit Document
// (per mockup). Both sides come from the A2 fetchDeliveryScheduleBatchDetails
// payload (`detail`), so no extra reads are needed:
//   · consumed  — detail.bomConsumptionDetails[].BomComponentCode, with its
//                 consumptionBatchNumbers[].componentBatchNumber as the batches
//   · PO BoM    — detail.bomComponentDetails[].BomComponentCode
// Both keys are LOWERCASE on the A2 response (same as PA4MaterialExpired.jsx).
// Sync PO and Sync Inventory here trigger recheck + reset (V5).
import React, { useMemo } from "react";
import { Typography } from "@material-ui/core";
import SectionShell from "../components/SectionShell";
import ActionTabs from "../components/common/ActionTabs";
import FieldCard from "../components/common/FieldCard";
import ConsumptionDocumentTab from "../components/common/ConsumptionDocumentTab";
import SyncActions from "../components/common/SyncActions";
import ResubmitDocumentButton from "../components/common/ResubmitDocumentButton";
import { GENERIC_ERROR } from "../utils/constants";

// Material codes can arrive as strings or numbers — compare them as trimmed strings.
const codeOf = (c) => (c && c.BomComponentCode != null ? String(c.BomComponentCode).trim() : "");
const arr = (v) => (Array.isArray(v) ? v : []);

const PA5MaterialNotInBoM = ({ row, detail, featuresAccess, viewOnly, onRefresh }) => {
  const bomMaterials = useMemo(
    () => arr(detail && detail.bomComponentDetails).map(codeOf).filter(Boolean),
    [detail]
  );

  const consumedDetails = useMemo(() => arr(detail && detail.bomConsumptionDetails), [detail]);
  const consumedCodes = consumedDetails.map(codeOf).filter(Boolean);
  // Every consumed material's batches, flattened in material order.
  const consumedBatches = consumedDetails
    .map((c) => arr(c && c.consumptionBatchNumbers).map((b) => b && b.componentBatchNumber).filter(Boolean).join(", "))
    .filter(Boolean);

  const notMatching = consumedCodes.filter((code) => bomMaterials.length > 0 && !bomMaterials.includes(code));

  const failTab = () => (
    <React.Fragment>
      <Typography style={{ color: "#6B7280", fontSize: "0.85rem", marginBottom: 12 }}>
        The consumed material reported for this batch is not part of the PO Bill of
        Material. Resolve the failure below and re-run the check.
      </Typography>
      {!detail ? (
        <Typography variant="body2" style={{ color: "#C0392B" }}>{GENERIC_ERROR}</Typography>
      ) : consumedCodes.length === 0 ? (
        <Typography variant="body2" style={{ color: "#777" }}>
          No consumed materials found. Sync PO if the PO Bill of Materials has been
          updated in SAP.
        </Typography>
      ) : (
        // All four fields on one row, values in the default black (mockup slide 13).
        <FieldCard
          columns={4}
          fields={[
            { label: "Consumed Material Number", value: consumedCodes.join(", ") },
            { label: "Consumed Material Batch Number", value: consumedBatches.join(", ") || "-" },
            { label: "Materials in PO BOM", value: bomMaterials.join(", ") || "-" },
            { label: "PO Number", value: row.scheduleAgreementNumber },
          ]}
        />
      )}
    </React.Fragment>
  );

  return (
    <SectionShell viewOnly={viewOnly}>
      <ActionTabs
        headRight={
          <React.Fragment>
            <SyncActions
              row={row}
              detail={detail}
              featuresAccess={featuresAccess}
              showSyncInventory
              showSyncPO
              resetsBatchInventory
              resetsBatchPO
              onRefresh={onRefresh}
            />
            <ResubmitDocumentButton row={row} detail={detail} featuresAccess={featuresAccess} onAfter={onRefresh} blue />
          </React.Fragment>
        }
        tabs={[
          { key: "finding", label: "Consumed Material Not Matching PO BOM", badge: notMatching.length || null, render: failTab },
          {
            key: "doc",
            label: "View Consumption Document",
            render: () => (
              <ConsumptionDocumentTab row={row} detail={detail} featuresAccess={featuresAccess} onRefresh={onRefresh} />
            ),
          },
        ]}
      />
    </SectionShell>
  );
};

export default PA5MaterialNotInBoM;
